/* eslint-disable @typescript-eslint/naming-convention */
import { isNumber } from 'lodash';
import { createCanvas } from '@/utils/file-kit';
import { type EdgelessCanvasService } from '..';
import { type IEdgelessPlugin } from '../../../shared';
import { type EdgelessObject } from '../edgeless-object';

export class MediaPlugin implements IEdgelessPlugin {
    private __requestAnimationFrameId: number = 0;
    private __inlineRequestAnimationFrameId: number = 0;
    private __mediaCanvas: HTMLCanvasElement | null = null;
    private __mediaCtx: CanvasRenderingContext2D | null = null;
    private __edgelessBufferCanvas: HTMLCanvasElement | null = null;
    private __edgelessBufferCtx: CanvasRenderingContext2D | null = null;
    private __frameBufferCanvas: HTMLCanvasElement | null = null;
    private __frameBufferCtx: CanvasRenderingContext2D | null = null;
    // private __objects: EdgelessObject[] = [];
    private __objectIdsMap: Map<string, { index: number }> = new Map<string, { index: number }>();
    private __objectsMap: Map<string, EdgelessObject> = new Map<string, EdgelessObject>();
    private __inlineObjects: Set<EdgelessObject> = new Set<EdgelessObject>();

    constructor(
        private __edgeless: EdgelessCanvasService,
        private __options: {
            /** 额外渲染处理回调，用于在插件内部渲染完成后，需要叠加额外的渲染处理 */
            onExtraRenderProcess: () => void;
        },
    ) {}

    private getSortInfo(object: EdgelessObject) {
        const { frame, flatLayer } = this.__edgeless.service;
        const layer = flatLayer.getLayer(object.layerId);
        const originIndex = layer?.__originIndex;
        if (!isNumber(originIndex)) return null;
        const renderIds = frame.getLayerIds(originIndex);
        let index = renderIds.indexOf(object.layerId);
        if (index === -1) {
            const wrapLayerId = flatLayer.getWrapLayer(object.layerId)?.id;
            if (wrapLayerId) {
                index = renderIds.indexOf(wrapLayerId);
            }
        }
        if (index === -1) return null;
        return {
            originIndex,
            index,
        };
    }

    private getObjectRenderGroups() {
        const { flatLayer } = this.__edgeless.service;
        const groups = new Map<number, { layerId: string; index: number }[]>();

        for (const [layerId, value] of this.__objectIdsMap) {
            const originIndex = flatLayer.getLayer(layerId)?.__originIndex;
            if (!isNumber(originIndex)) continue;
            groups.set(originIndex, [...(groups.get(originIndex) || []), { layerId, index: value.index }]);
        }

        return Array.from(groups.entries())
            .map(([frameId, values]) => ({
                frameId,
                values: values.sort((a, b) => a.index - b.index),
            }))
            .sort((a, b) => a.frameId - b.frameId);
    }

    add(object: EdgelessObject) {
        if (!this.__edgeless.isEdgelessMode) {
            this.__addInlineObject(object);
            return;
        }
        this.__addOverlayObject(object);
    }

    private __addOverlayObject(object: EdgelessObject) {
        const sortInfo = this.getSortInfo(object);
        if (!sortInfo) return;
        const { originIndex, index } = sortInfo;
        this.__objectIdsMap.set(object.layerId, { index });
        this.__objectsMap.set(object.layerId, object);
        this.__edgeless.addDirtyFrameId(originIndex);
        this.__edgeless.requestRenderAll();
    }

    remove(object: EdgelessObject) {
        this.__removeInlineObject(object);
        // removeFromArray(this.__objects, object);
        const originIndex = this.__edgeless.service.flatLayer.getLayer(object.layerId)?.__originIndex;
        this.__objectIdsMap.delete(object.layerId);
        this.__objectsMap.delete(object.layerId);
        if (isNumber(originIndex)) {
            this.__edgeless.addDirtyFrameId(originIndex);
        }
        this.__edgeless.requestRenderAll();
    }

    render() {
        cancelAnimationFrame(this.__requestAnimationFrameId);

        if (!this.__edgeless.isEdgelessMode) {
            this.__migrateOverlayObjectsToInline();
            return this.__dispose();
        }

        this.__migrateInlineObjectsToOverlay();

        if (!this.__objectsMap.size) {
            return this.__dispose();
        }

        if (!this.__edgelessBufferCanvas) {
            const { canvas, ctx } = createCanvas(this.__edgeless.canvas.width, this.__edgeless.canvas.height);
            this.__edgelessBufferCanvas = canvas;
            this.__edgelessBufferCtx = ctx;
        }

        this.__edgelessBufferCanvas.width = this.__edgeless.canvas.width;
        this.__edgelessBufferCanvas.height = this.__edgeless.canvas.height;

        this.__edgelessBufferCtx?.clearRect(
            0,
            0,
            this.__edgelessBufferCanvas.width,
            this.__edgelessBufferCanvas.height,
        );
        this.__edgelessBufferCtx?.drawImage(this.__edgeless.canvas, 0, 0);

        this.__renderObjects(true);
    }

    private __renderObjects(skipAnimation: boolean = false) {
        if (!this.__objectsMap.size) {
            return this.__dispose();
        }

        if (!this.__edgelessBufferCanvas) return;

        if (!this.__mediaCanvas) {
            const { canvas, ctx } = createCanvas(this.__edgeless.canvas.width, this.__edgeless.canvas.height);
            this.__mediaCanvas = canvas;
            this.__mediaCtx = ctx;
        }

        this.__mediaCanvas.width = this.__edgeless.canvas.width;
        this.__mediaCanvas.height = this.__edgeless.canvas.height;

        if (this.__mediaCtx) {
            const { flatLayer, frame: frameOperate } = this.__edgeless.service;

            if (frameOperate.size) {
                if (!this.__frameBufferCanvas) {
                    const { canvas, ctx } = createCanvas(this.__edgeless.canvas.width, this.__edgeless.canvas.height);
                    this.__frameBufferCanvas = canvas;
                    this.__frameBufferCtx = ctx;
                }
                this.__frameBufferCanvas.width = this.__edgeless.canvas.width;
                this.__frameBufferCanvas.height = this.__edgeless.canvas.height;
                this.__frameBufferCtx?.clearRect(0, 0, this.__frameBufferCanvas.width, this.__frameBufferCanvas.height);
            } else {
                this.__frameBufferCanvas = null;
                this.__frameBufferCtx = null;
            }

            this.__mediaCtx.clearRect(0, 0, this.__mediaCanvas.width, this.__mediaCanvas.height);
            this.__mediaCtx.drawImage(this.__edgelessBufferCanvas, 0, 0);

            this.getObjectRenderGroups().forEach(({ frameId, values }) => {
                for (const value of values) {
                    const object = this.__objectsMap.get(value.layerId);
                    const layer = flatLayer.getLayer(value.layerId);
                    if (!object || !layer) continue;
                    if (this.__frameBufferCtx) {
                        const clipFrameId = layer.__stickyFrameId ?? frameId;
                        const frame = frameOperate.get(clipFrameId);

                        if (!frame) {
                            this.__edgeless.renderObject(this.__mediaCtx!, object);
                            continue;
                        }

                        frame.renderObjectsByClip(this.__frameBufferCtx, [object]);
                    } else {
                        this.__edgeless.renderObject(this.__mediaCtx!, object);
                    }
                }
            });
            if (this.__frameBufferCanvas) {
                this.__mediaCtx.drawImage(this.__frameBufferCanvas, 0, 0);
            }
            this.__edgeless.clearContext();
            this.__edgeless.getContext().drawImage(this.__mediaCanvas, 0, 0);
        }

        if (!skipAnimation) {
            this.__options.onExtraRenderProcess();
        }

        this.__requestAnimationFrameId = requestAnimationFrame(() => this.__renderObjects());
    }

    private __addInlineObject(object: EdgelessObject) {
        this.__inlineObjects.add(object);
        this.__startInlineRender();
    }

    private __removeInlineObject(object: EdgelessObject) {
        this.__inlineObjects.delete(object);
        if (this.__inlineObjects.size || !this.__inlineRequestAnimationFrameId) return;

        cancelAnimationFrame(this.__inlineRequestAnimationFrameId);
        this.__inlineRequestAnimationFrameId = 0;
    }

    private __startInlineRender() {
        if (this.__inlineRequestAnimationFrameId) return;

        const render = () => {
            if (this.__inlineObjects.size && !this.__edgeless.isEdgelessMode) {
                this.__markInlineObjectsDirty();
                this.__inlineRequestAnimationFrameId = requestAnimationFrame(render);
            } else {
                this.__inlineRequestAnimationFrameId = 0;
            }
        };

        this.__inlineRequestAnimationFrameId = requestAnimationFrame(render);
    }

    private __markInlineObjectsDirty() {
        const dirtyFrameIds = new Set<number>();
        const { flatLayer } = this.__edgeless.service;

        this.__inlineObjects.forEach((object) => {
            const layer = flatLayer.getLayer(object.layerId);
            if (!layer) return;
            if (isNumber(layer.__originIndex)) {
                dirtyFrameIds.add(layer.__originIndex);
            }
        });

        dirtyFrameIds.forEach((frameId) => this.__edgeless.addDirtyFrameId(frameId));
        this.__edgeless.requestRenderAll();
    }

    private __migrateOverlayObjectsToInline() {
        if (!this.__objectsMap.size) return;

        this.__objectsMap.forEach((object) => this.__addInlineObject(object));
        this.__objectIdsMap.clear();
        this.__objectsMap.clear();
    }

    private __migrateInlineObjectsToOverlay() {
        if (!this.__inlineObjects.size) return;

        const objects = Array.from(this.__inlineObjects);
        this.__inlineObjects.clear();
        if (this.__inlineRequestAnimationFrameId) {
            cancelAnimationFrame(this.__inlineRequestAnimationFrameId);
            this.__inlineRequestAnimationFrameId = 0;
        }
        objects.forEach((object) => this.__addOverlayObject(object));
    }

    private __dispose() {
        this.__mediaCanvas = null;
        this.__mediaCtx = null;
        this.__edgelessBufferCanvas = null;
        this.__edgelessBufferCtx = null;
        this.__frameBufferCanvas = null;
        this.__frameBufferCtx = null;
    }
}
